import Decimal from "decimal.js";
import { z } from "zod";

// ──────────────────────────── Cede Arena Spin & Go ────────────────────────────
//
// Pot-multiplier wheel that resizes an arena competition's prize pool. Per-category
// odds config (Solo CMS) + a pregenerated, auditable pool of drawn rows consumed at lock.
// Duel categories draw the BUY-IN multiple players see (pool = multiple × stake); room
// categories, whose roster is unknown at draw, draw a pot factor (pool = factor × collected).

/** The Spin & Go categories, one odds config + pregen pool each. Mirrors the Prisma enum. */
export const SpinAndGoCategorySchema = z.enum(["QUICK_MATCH_1V1", "QUICK_MATCH_1V1V1", "PRIVATE_ROOM", "OPT_IN"]);
export type SpinAndGoCategory = z.infer<typeof SpinAndGoCategorySchema>;

/**
 * Seats per category: a duel category's fixed roster, or null for a room category
 * (variable roster). The single source of each category's odds unit — a duel draws
 * buy-in multiples, a room draws pot factors.
 */
export const SPIN_AND_GO_CATEGORY_SEATS = {
  QUICK_MATCH_1V1: 2,
  QUICK_MATCH_1V1V1: 3,
  PRIVATE_ROOM: null,
  OPT_IN: null,
} as const satisfies Record<SpinAndGoCategory, number | null>;

/** Seats of a duel category, or null for a room category (whose odds stay pot factors). */
export const spinAndGoDuelSeats = (category: SpinAndGoCategory): number | null => SPIN_AND_GO_CATEGORY_SEATS[category];

/** DB `Decimal(12,4)` = 8 integer digits max, so the value must stay below 10^8. */
const DECIMAL_12_4_UPPER_BOUND = 100_000_000;

/**
 * A strictly-positive decimal string (Prisma Decimal over the wire), e.g. "0.5",
 * "1", "2.25". Capped at 4 fractional digits AND below 10^8 to match the DB
 * `Decimal(12,4)` column — a longer factor would be silently truncated on persist
 * and an out-of-range one would overflow the column.
 */
const PositiveDecimalStringSchema = z
  .string()
  .regex(/^\d+(?:\.\d{1,4})?$/, { error: "must be a non-negative decimal string with at most 4 decimals" })
  .refine((value) => new Decimal(value).greaterThan(0), { error: "must be greater than 0" })
  .refine((value) => new Decimal(value).lessThan(DECIMAL_12_4_UPPER_BOUND), {
    error: "must be less than 100000000 (Decimal(12,4) range)",
  });

/**
 * Max fractional digits allowed on an odds-table PROBABILITY. Unlike a multiple
 * (persisted to a Decimal(12,4) column), probabilities live only in the odds-table
 * JSON, so this is a validation choice, not a DB limit — kept generous to support
 * tiny-odds / big-jackpot wheels (e.g. a ×10000 factor at 1e-9). The CMS mirrors
 * this constant so client + server stay in sync.
 */
export const SPIN_AND_GO_PROBABILITY_MAX_DECIMALS = 12;

/** A probability decimal string in (0, 1] with at most SPIN_AND_GO_PROBABILITY_MAX_DECIMALS decimals. */
const ProbabilityDecimalStringSchema = z
  .string()
  .regex(new RegExp(`^\\d+(?:\\.\\d{1,${SPIN_AND_GO_PROBABILITY_MAX_DECIMALS}})?$`), {
    error: `must be a non-negative decimal string with at most ${SPIN_AND_GO_PROBABILITY_MAX_DECIMALS} decimals`,
  })
  .refine(
    (value) => {
      const probability = new Decimal(value);
      return probability.greaterThan(0) && probability.lessThanOrEqualTo(1);
    },
    { error: "probability must be greater than 0 and at most 1" },
  );

/**
 * One stored row of a category's odds table, decimal strings throughout:
 *  - `buyInMultiple` — duel categories only: the multiple players see (pool = multiple × stake).
 *  - `potFactor`     — room categories: pool = factor × collected. On a duel row it is
 *    `buyInMultiple ÷ seats` rounded down to 4 dp, still written for API pods that predate
 *    `buyInMultiple`.
 *  - `probability`   — the row's share of the wheel.
 */
export const SpinAndGoOddsRowSchema = z.object({
  potFactor: PositiveDecimalStringSchema,
  buyInMultiple: PositiveDecimalStringSchema.optional(),
  probability: ProbabilityDecimalStringSchema,
});
export type SpinAndGoOddsRow = z.infer<typeof SpinAndGoOddsRowSchema>;

/**
 * One odds row as Solo writes it: a duel category sends `buyInMultiple` (the API derives
 * `potFactor`), a room category sends `potFactor`. {@link spinAndGoOddsRowsUnitIssue}
 * enforces which one per category.
 */
export const SpinAndGoOddsRowInputSchema = z.object({
  potFactor: PositiveDecimalStringSchema.optional(),
  buyInMultiple: PositiveDecimalStringSchema.optional(),
  probability: ProbabilityDecimalStringSchema,
});
export type SpinAndGoOddsRowInput = z.infer<typeof SpinAndGoOddsRowInputSchema>;

/** Min/max rows of a drawable odds table. */
export const SPIN_AND_GO_ODDS_MIN_ROWS = 2;
export const SPIN_AND_GO_ODDS_MAX_ROWS = 12;

/** A drawable table: 2–12 rows whose probabilities sum to exactly 1. */
export const isDrawableSpinAndGoOddsTable = (rows: readonly { probability: string }[]): boolean =>
  rows.length >= SPIN_AND_GO_ODDS_MIN_ROWS &&
  rows.length <= SPIN_AND_GO_ODDS_MAX_ROWS &&
  rows.reduce((sum, row) => sum.plus(row.probability), new Decimal(0)).equals(1);

/**
 * Why the rows carry the wrong unit for the category, or null when they're right: a duel
 * row needs `buyInMultiple`, a room row needs `potFactor` and no multiple. `input` mode also
 * rejects a duel row's `potFactor` — the API derives it, Solo never sends it.
 */
export const spinAndGoOddsRowsUnitIssue = (
  category: SpinAndGoCategory,
  rows: readonly { potFactor?: string; buyInMultiple?: string }[],
  mode: "stored" | "input",
): string | null => {
  const isDuel = spinAndGoDuelSeats(category) !== null;
  for (const row of rows) {
    if (isDuel && row.buyInMultiple === undefined) return `${category} odds rows need a buyInMultiple`;
    if (isDuel && mode === "input" && row.potFactor !== undefined) {
      return `${category} odds rows carry a buyInMultiple, not a potFactor`;
    }
    if (!isDuel && row.buyInMultiple !== undefined)
      return `${category} odds rows carry a potFactor, not a buyInMultiple`;
    if (!isDuel && row.potFactor === undefined) return `${category} odds rows need a potFactor`;
  }
  return null;
};

/**
 * A finite probability grid can leave a few-bp edge no rounded table removes
 * (a residual on a probability is amplified by its value), so ≤5bp counts as fair.
 */
export const SPIN_AND_GO_FAIR_EDGE_TOLERANCE = new Decimal("0.0005");

/**
 * Why the wheel pays out above fair beyond {@link SPIN_AND_GO_FAIR_EDGE_TOLERANCE}, or
 * null. Fair: E[buy-in multiple] = seats on a duel, E[pot factor] = 1 on a room. Rows
 * missing the category's unit are {@link spinAndGoOddsRowsUnitIssue}'s to report.
 */
export const spinAndGoOddsAboveFairIssue = (
  category: SpinAndGoCategory,
  rows: readonly { potFactor?: string; buyInMultiple?: string; probability: string }[],
): string | null => {
  const seats = spinAndGoDuelSeats(category);
  const fair = new Decimal(seats ?? 1);
  let expected = new Decimal(0);
  for (const row of rows) {
    const value = seats === null ? row.potFactor : row.buyInMultiple;
    if (value === undefined) return null;
    expected = expected.plus(new Decimal(value).times(row.probability));
  }
  if (expected.dividedBy(fair).minus(1).lessThanOrEqualTo(SPIN_AND_GO_FAIR_EDGE_TOLERANCE)) return null;
  const unit = seats === null ? "pot factor" : "buy-in multiple";
  return `${category} pays out above fair: expected ${unit} ${expected.toString()} vs fair ${fair.toString()}`;
};

/**
 * A validated, non-empty odds table: 2–12 rows whose probabilities sum to exactly
 * 1. An enabled category must hold one of these; a disabled category may hold an
 * empty table (see {@link UpdateSpinAndGoConfigBodySchema}).
 */
export const SpinAndGoOddsTableSchema = z.array(SpinAndGoOddsRowSchema).refine(isDrawableSpinAndGoOddsTable, {
  error: "an odds table needs 2–12 rows whose probabilities sum to 1",
  path: ["oddsTable"],
});
export type SpinAndGoOddsTable = z.infer<typeof SpinAndGoOddsTableSchema>;

/**
 * One category's Spin & Go config as served by the CMS `GET /v1/cms/spin-and-go-config`.
 * `availableCount` is the live pregen-pool depth (unconsumed, unvoided, unreserved);
 * `oddsTable` may be empty for a disabled category.
 */
export const SpinAndGoConfigDtoSchema = z.object({
  category: SpinAndGoCategorySchema,
  enabled: z.boolean(),
  oddsTable: z.array(SpinAndGoOddsRowSchema),
  /** Pool depth at/below which the low-watermark alert fires. */
  lowWatermark: z.number().int().nonnegative(),
  /** Live count of claimable pregenerated multipliers for this category. */
  availableCount: z.number().int().nonnegative(),
  /** Last time the low-watermark alert fired (ISO), or null. */
  lowWatermarkNotifiedAt: z.string().nullable(),
});
export type SpinAndGoConfigDto = z.infer<typeof SpinAndGoConfigDtoSchema>;

/** Response for `GET|PUT /v1/cms/spin-and-go-config` — all three categories. */
export const GetSpinAndGoConfigsResponseSchema = z.object({
  configs: z.array(SpinAndGoConfigDtoSchema),
});
export type GetSpinAndGoConfigsResponse = z.infer<typeof GetSpinAndGoConfigsResponseSchema>;

/**
 * Body for `PUT /v1/cms/spin-and-go-config` — upserts a single category's config.
 * Rows carry the category's unit (see {@link SpinAndGoOddsRowInputSchema}). An enabled
 * category requires a valid 2–12 row odds table summing to 1 and not above fair; a
 * disabled category may pass an empty table (draws never run while disabled).
 */
export const UpdateSpinAndGoConfigBodySchema = z
  .object({
    category: SpinAndGoCategorySchema,
    enabled: z.boolean(),
    oddsTable: z.array(SpinAndGoOddsRowInputSchema),
    lowWatermark: z.number().int().nonnegative(),
  })
  .superRefine((body, ctx) => {
    const unitIssue = spinAndGoOddsRowsUnitIssue(body.category, body.oddsTable, "input");
    if (unitIssue !== null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["oddsTable"], message: unitIssue });
    }
    if (body.enabled && !isDrawableSpinAndGoOddsTable(body.oddsTable)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["oddsTable"],
        message: "an enabled category needs a 2–12 row odds table whose probabilities sum to 1",
      });
    }
    // Disabled may hold any valid odds: nothing draws from it.
    const aboveFair = body.enabled ? spinAndGoOddsAboveFairIssue(body.category, body.oddsTable) : null;
    if (aboveFair !== null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["oddsTable"], message: aboveFair });
    }
  });
export type UpdateSpinAndGoConfigBody = z.infer<typeof UpdateSpinAndGoConfigBodySchema>;

/**
 * Public per-category Spin & Go config served by `GET /v1/spin-and-go/public-config`.
 * Drives the arena FE's wheel preview + availability gate:
 *  - `enabled`   — master feature flag AND the category toggle.
 *  - `available` — claimable pregen-pool depth (unconsumed, unvoided, unreserved).
 *  - `oddsTable` — the category's public odds (may be empty for a disabled category).
 */
export const SpinAndGoPublicConfigResponseSchema = z.object({
  enabled: z.boolean(),
  available: z.number().int().nonnegative(),
  oddsTable: z.array(SpinAndGoOddsRowSchema),
});
export type SpinAndGoPublicConfigResponse = z.infer<typeof SpinAndGoPublicConfigResponseSchema>;

/** One pregenerated multiplier row in the CMS audit list. All timestamps are ISO strings. */
export const SpinAndGoMultiplierDtoSchema = z.object({
  id: z.string(),
  category: SpinAndGoCategorySchema,
  potFactor: z.string(),
  /** The drawn buy-in multiple on a duel category; null on a room. Optional for the deploy window (older API). */
  buyInMultiple: z.string().nullish(),
  reservedByCampaignName: z.string().nullable(),
  reservedAt: z.string().nullable(),
  consumedAt: z.string().nullable(),
  voidedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type SpinAndGoMultiplierDto = z.infer<typeof SpinAndGoMultiplierDtoSchema>;

/**
 * Response for `GET|POST /v1/cms/spin-and-go-multipliers` — the audit list of
 * unconsumed rows for a category plus pool-health stats, in the category's unit
 * (buy-in multiple for a duel, pot factor for a room). `realizedAvgFactor` is the
 * mean drawn value across the listed rows; `worstCaseSubsidyFactor` is the largest
 * one still in the pool (the biggest top-up a claim could cost). Both are "0" for
 * an empty pool.
 */
export const GetSpinAndGoUnconsumedResponseSchema = z.object({
  category: SpinAndGoCategorySchema,
  rows: z.array(SpinAndGoMultiplierDtoSchema),
  availableCount: z.number().int().nonnegative(),
  realizedAvgFactor: z.string(),
  worstCaseSubsidyFactor: z.string(),
});
export type GetSpinAndGoUnconsumedResponse = z.infer<typeof GetSpinAndGoUnconsumedResponseSchema>;

/** Body for `POST /v1/cms/spin-and-go-multipliers` — generate a batch from the current odds table. */
export const GenerateSpinAndGoMultipliersBodySchema = z.object({
  category: SpinAndGoCategorySchema,
  count: z.number().int().positive().max(10_000),
});
export type GenerateSpinAndGoMultipliersBody = z.infer<typeof GenerateSpinAndGoMultipliersBodySchema>;

/** Body for `POST /v1/cms/spin-and-go-multipliers#void` — void specific pregenerated rows. */
export const VoidSpinAndGoMultipliersBodySchema = z.object({
  category: SpinAndGoCategorySchema,
  ids: z.array(z.string()).min(1),
});
export type VoidSpinAndGoMultipliersBody = z.infer<typeof VoidSpinAndGoMultipliersBodySchema>;
